/* ==========================================================================
   Product gallery (sections/product-template.liquid, style "Default")
   Vanilla JS, no dependencies. One gallery per section: [data-product-gallery].
   - Buttons Fotos / Video (aria-pressed) show one panel each; leaving the
     video pauses it. Medidas (aria-expanded) opens the size guide, a modal
     <dialog>; the gallery stays as it was (a playing video pauses).
   - Photos: a horizontal scroll with snap, one photo per view (swipe on
     touch screens). The active photo follows the scroll; thumbnails move it.
   - Thumbnails: whole thumbs only, N per view (section setting), pages with
     window.DotPager (theme.js.liquid) and arrows, as the search shortcuts.
   - Zoom (mouse only): the photo scales inside its own box following the
     cursor; the 2048px version is requested on the first hover.
   - Video: built from its <template> the first time its button is pressed,
     over its cover image (Liquid); YouTube's player shows once it plays.
   - Cart: the hidden #ProductPhotoImg always has the active photo, which
     Shopify.cartPopapForm (cart.api.js) shows in the add-to-cart modal.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = '[data-product-gallery]';
  var finePointer = window.matchMedia
    ? window.matchMedia('(hover: hover) and (pointer: fine)')
    : { matches: false };
  var reducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  function behavior(smooth) {
    return smooth && !reducedMotion.matches ? 'smooth' : 'auto';
  }

  // File name without folder or query: matches a variant's featured_image.src
  // with a photo (data-pg-file) even if their ids differ (media vs. image id)
  function fileKey(src) {
    return String(src || '').split('?')[0].split('/').pop();
  }

  function Gallery(root) {
    this.root = root;
    this.track = root.querySelector('[data-pg-track]');
    this.stage = root.querySelector('[data-pg-stage]');
    this.slides = root.querySelectorAll('[data-pg-slide]');
    this.thumbs = root.querySelectorAll('[data-pg-thumb]');
    this.thumbBox = root.querySelector('[data-pg-thumbs]');
    this.thumbTrack = this.thumbBox && this.thumbBox.querySelector('[data-pager-track]');
    this.cartImage = root.querySelector('.product_variant_image');
    this.perView = parseInt(root.getAttribute('data-pg-per-view'), 10) || 4;
    this.index = 0;
    this.target = null; // slide a programmatic scroll is heading to
    this.pager = null;

    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i].classList.contains('is-active')) this.index = i;
    }

    this.bindTabs();
    this.bindGuide();
    if (this.track) this.bindPhotos();
    if (this.thumbTrack) this.bindThumbs();
    if (this.stage && root.hasAttribute('data-pg-zoom')) this.bindZoom();
  }

  /* Buttons and panels ----------------------------------------------------- */

  Gallery.prototype.bindTabs = function () {
    var self = this;
    this.tabs = this.root.querySelectorAll('[data-pg-tab]');
    for (var i = 0; i < this.tabs.length; i++) {
      this.tabs[i].addEventListener('click', function () {
        self.setTab(this.getAttribute('data-pg-tab'));
      });
    }
  };

  Gallery.prototype.setTab = function (name) {
    var current = this.root.querySelector('[data-pg-tab][aria-pressed="true"]');
    if (current && current.getAttribute('data-pg-tab') === name) return;
    for (var i = 0; i < this.tabs.length; i++) {
      var on = this.tabs[i].getAttribute('data-pg-tab') === name;
      this.tabs[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      this.tabs[i].classList.toggle('option-active', on);
    }
    var panels = this.root.querySelectorAll('[data-pg-panel]');
    for (var j = 0; j < panels.length; j++) {
      panels[j].hidden = panels[j].getAttribute('data-pg-panel') !== name;
    }
    if (name === 'video') {
      this.playVideo();
    } else {
      this.pauseVideo();
    }
    // The photos were hidden: back on the active photo and its thumbnail
    if (name === 'photos' && this.track) {
      this.track.scrollLeft = this.index * this.track.clientWidth;
      this.revealThumb(this.index, false);
      this.updateArrows();
    }
  };

  /* Photos ----------------------------------------------------------------- */

  Gallery.prototype.bindPhotos = function () {
    var self = this;
    var ticking = false;
    var settle = null;

    this.track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var width = self.track.clientWidth;
        if (!width) return;
        var i = Math.round(self.track.scrollLeft / width);
        // While a thumbnail's scroll runs, the photos it passes don't count
        if (self.target !== null) {
          if (i !== self.target) return;
          self.target = null;
        }
        if (i !== self.index) self.setActive(i, true);
      });
    }, { passive: true });

    // Keep the active photo in place when the box changes width
    var keep = function () {
      if (self.track.clientWidth) self.track.scrollLeft = self.index * self.track.clientWidth;
    };
    if (window.ResizeObserver) {
      new ResizeObserver(keep).observe(this.track);
    } else {
      window.addEventListener('resize', keep);
    }
    keep();

    // If a smooth scroll is interrupted (a finger), stop waiting for it
    this.clearTarget = function () {
      clearTimeout(settle);
      settle = setTimeout(function () { self.target = null; }, 1000);
    };
  };

  Gallery.prototype.show = function (i, smooth) {
    if (!this.track || i < 0 || i >= this.slides.length) return;
    this.setActive(i, smooth);
    var left = i * this.track.clientWidth;
    if (Math.abs(this.track.scrollLeft - left) < 2) return;
    this.target = i;
    this.clearTarget();
    this.track.scrollTo({ left: left, behavior: behavior(smooth) });
  };

  Gallery.prototype.setActive = function (i, smooth) {
    if (i < 0 || i >= this.slides.length) return;
    if (i !== this.index) {
      this.slides[this.index].classList.remove('is-active');
      this.slides[i].classList.add('is-active');
      this.index = i;
      if (this.stage) this.stage.classList.remove('is-zooming');
    }
    for (var t = 0; t < this.thumbs.length; t++) {
      var on = t === i;
      this.thumbs[t].classList.toggle('is-active', on);
      if (on) {
        this.thumbs[t].setAttribute('aria-current', 'true');
      } else {
        this.thumbs[t].removeAttribute('aria-current');
      }
    }
    var cartSrc = this.slides[i].getAttribute('data-cart-src');
    if (this.cartImage && cartSrc) this.cartImage.setAttribute('src', cartSrc);
    this.revealThumb(i, smooth);
  };

  /* Thumbnails ------------------------------------------------------------- */

  Gallery.prototype.bindThumbs = function () {
    var self = this;
    if (window.DotPager) this.pager = new window.DotPager(this.thumbBox);

    for (var i = 0; i < this.thumbs.length; i++) {
      this.thumbs[i].addEventListener('click', function () {
        self.show(parseInt(this.getAttribute('data-pg-thumb'), 10), true);
      });
    }

    this.prev = this.thumbBox.querySelector('[data-pg-prev]');
    this.next = this.thumbBox.querySelector('[data-pg-next]');
    if (this.prev) this.prev.addEventListener('click', function () { self.stepPage(-1); });
    if (this.next) this.next.addEventListener('click', function () { self.stepPage(1); });

    var ticking = false;
    this.thumbTrack.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        self.updateArrows();
      });
    }, { passive: true });
    window.addEventListener('resize', function () { self.updateArrows(); });

    this.revealThumb(this.index, false);
    this.updateArrows();
  };

  Gallery.prototype.pageWidth = function () {
    if (this.pager) return this.pager.pageWidth();
    var gap = parseFloat(window.getComputedStyle(this.thumbTrack).columnGap) || 0;
    return this.thumbTrack.clientWidth + gap;
  };

  Gallery.prototype.pageCount = function () {
    return Math.ceil(this.thumbs.length / this.perView);
  };

  Gallery.prototype.currentPage = function () {
    if (this.pager) return this.pager.current();
    var t = this.thumbTrack;
    if (t.scrollLeft >= t.scrollWidth - t.clientWidth - 2) return this.pageCount() - 1;
    return Math.round(t.scrollLeft / this.pageWidth());
  };

  // The last page can be shorter: never past the end (as DotPager.goTo)
  Gallery.prototype.goPage = function (page, smooth) {
    var t = this.thumbTrack;
    if (!t) return;
    page = Math.max(0, Math.min(page, this.pageCount() - 1));
    if (this.pager && smooth) {
      this.pager.goTo(page);
      return;
    }
    t.scrollTo({
      left: Math.min(page * this.pageWidth(), t.scrollWidth - t.clientWidth),
      behavior: behavior(smooth)
    });
  };

  Gallery.prototype.stepPage = function (step) {
    this.goPage(this.currentPage() + step, true);
  };

  // Bring the page with this thumbnail into view (only if it's hidden)
  Gallery.prototype.revealThumb = function (i, smooth) {
    var t = this.thumbTrack;
    var thumb = this.thumbs[i];
    if (!t || !thumb || !t.clientWidth) return;
    var left = thumb.offsetLeft; // the track is positioned (product-gallery.css)
    var visible = left >= t.scrollLeft - 1 &&
      left + thumb.offsetWidth <= t.scrollLeft + t.clientWidth + 1;
    if (!visible) this.goPage(Math.floor(i / this.perView), smooth);
  };

  // Prev hidden at the start, next at the end
  Gallery.prototype.updateArrows = function () {
    var t = this.thumbTrack;
    if (!t || !t.clientWidth) return;
    var max = t.scrollWidth - t.clientWidth;
    if (this.prev) this.prev.classList.toggle('is-off', t.scrollLeft <= 1);
    if (this.next) this.next.classList.toggle('is-off', t.scrollLeft >= max - 2);
  };

  /* Zoom (mouse only) ------------------------------------------------------ */

  Gallery.prototype.bindZoom = function () {
    var self = this;
    var stage = this.stage;

    function move(e) {
      if (!finePointer.matches || !self.slides.length) return;
      var slide = self.slides[self.index];
      var photo = slide.querySelector('.pg-photo');
      if (!photo) return;
      self.loadZoom(slide, photo);
      var r = stage.getBoundingClientRect();
      var x = Math.max(0, Math.min(100, (e.clientX - r.left) / r.width * 100));
      var y = Math.max(0, Math.min(100, (e.clientY - r.top) / r.height * 100));
      photo.style.transformOrigin = x + '% ' + y + '%';
      stage.classList.add('is-zooming');
    }

    stage.addEventListener('mouseenter', move);
    stage.addEventListener('mousemove', move);
    stage.addEventListener('mouseleave', function () {
      stage.classList.remove('is-zooming');
    });
  };

  // The big version once per photo, swapped in only when it's loaded
  Gallery.prototype.loadZoom = function (slide, photo) {
    if (slide.hasAttribute('data-pg-zoom-loaded')) return;
    slide.setAttribute('data-pg-zoom-loaded', '');
    var src = slide.getAttribute('data-zoom-src');
    if (!src) return;
    var big = new Image();
    big.onload = function () {
      photo.setAttribute('srcset', src + ' 2048w');
    };
    big.src = src;
  };

  /* Size guide ------------------------------------------------------------- */

  // A modal <dialog>: showModal() puts it in the top layer and makes the rest
  // of the page inert (focus stays inside); .is-open on the next frame slides
  // it in. Closing (X, Esc, the backdrop) removes .is-open and calls close()
  // when the slide ends; focus goes back to the Medidas button.
  Gallery.prototype.bindGuide = function () {
    var self = this;
    this.guide = this.root.querySelector('[data-pg-guide]');
    this.guideButton = this.root.querySelector('[data-pg-guide-open]');
    if (!this.guide || !this.guideButton) return;
    // No <dialog> support (Safari < 15.4): no button rather than a dead one
    if (typeof this.guide.showModal !== 'function') {
      this.guideButton.hidden = true;
      return;
    }
    this.guidePanel = this.guide.querySelector('.pg-guide__panel');
    this.guideClose = this.guide.querySelector('.pg-guide__close');
    this.guideClosing = false;
    this.guideTimer = null;

    this.guideButton.addEventListener('click', function () {
      self.openGuide();
    });
    var closers = this.guide.querySelectorAll('[data-pg-guide-close]');
    for (var i = 0; i < closers.length; i++) {
      closers[i].addEventListener('click', function () {
        self.closeGuide();
      });
    }
    // Esc: animate the closing instead of the browser's instant one
    this.guide.addEventListener('cancel', function (e) {
      e.preventDefault();
      self.closeGuide();
    });
    // Every way it ends up closed (also if the browser closes it anyway)
    this.guide.addEventListener('close', function () {
      self.guideClosed();
    });
    this.bindGuideTabs();
  };

  Gallery.prototype.openGuide = function () {
    var guide = this.guide;
    if (!guide || typeof guide.showModal !== 'function' || guide.open) return;
    this.pauseVideo();
    document.documentElement.classList.add('pg-guide-open');
    guide.showModal();
    this.guideButton.setAttribute('aria-expanded', 'true');
    // Focus on the close button without scrolling: the panel is still off
    // screen (no autofocus, which would scroll it into view)
    if (this.guideClose) this.guideClose.focus({ preventScroll: true });
    // Flush styles: the slide starts from the off-screen position
    void this.guidePanel.offsetWidth;
    guide.classList.add('is-open');
  };

  Gallery.prototype.closeGuide = function () {
    var self = this;
    var guide = this.guide;
    var panel = this.guidePanel;
    if (!guide || !guide.open || this.guideClosing) return;
    this.guideClosing = true;
    guide.classList.remove('is-open');

    function done() {
      if (!self.guideClosing) return;
      panel.removeEventListener('transitionend', onEnd);
      clearTimeout(self.guideTimer);
      if (guide.open) guide.close();
    }
    function onEnd(e) {
      if (e.target === panel && e.propertyName === 'transform') done();
    }
    if (reducedMotion.matches) {
      done();
      return;
    }
    panel.addEventListener('transitionend', onEnd);
    this.guideTimer = setTimeout(done, 450);
  };

  Gallery.prototype.guideClosed = function () {
    this.guideClosing = false;
    clearTimeout(this.guideTimer);
    this.guide.classList.remove('is-open');
    document.documentElement.classList.remove('pg-guide-open');
    this.guideButton.setAttribute('aria-expanded', 'false');
    this.guideButton.focus();
  };

  // Tabs (2 or 3 images): click, ← / → (wrapping), Home and End. Only the
  // selected tab is in the tab order.
  Gallery.prototype.bindGuideTabs = function () {
    var tabs = this.guide.querySelectorAll('[role="tab"]');
    if (!tabs.length) return;

    function select(index, focus) {
      for (var j = 0; j < tabs.length; j++) {
        var on = j === index;
        tabs[j].setAttribute('aria-selected', on ? 'true' : 'false');
        tabs[j].tabIndex = on ? 0 : -1;
        var panel = document.getElementById(tabs[j].getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      }
      if (focus) tabs[index].focus();
    }

    Array.prototype.forEach.call(tabs, function (tab, i) {
      tab.addEventListener('click', function () {
        select(i, false);
      });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        select(next, true);
      });
    });
  };

  /* Video ------------------------------------------------------------------ */

  Gallery.prototype.videoBox = function () {
    return this.root.querySelector('[data-pg-video]');
  };

  Gallery.prototype.playVideo = function () {
    var box = this.videoBox();
    if (!box) return;
    var player = box.querySelector('iframe, video');
    if (!player) {
      // First time: build it (the iframe starts on its own with autoplay=1)
      var tpl = box.querySelector('template');
      if (!tpl) return;
      var content = tpl.content.cloneNode(true);
      var video = content.querySelector('video');
      var frame = content.querySelector('iframe');
      // Shopify video: without its own poster (the cover image is already
      // behind it), so the cover isn't downloaded twice
      if (video) {
        video.removeAttribute('poster');
        video.setAttribute('playsinline', '');
      }
      box.appendChild(content);
      if (frame) this.revealFrame(box, frame);
      if (video) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }
      return;
    }
    this.command(box, player, 'play');
  };

  // YouTube's player is black while it starts: .is-ready shows it when it
  // reports it's playing (it reports after "listening", enablejsapi=1), or
  // 2.5s after it loads (autoplay blocked: then it shows its own cover and
  // play button). Vimeo: its first message. Never later than 6s.
  Gallery.prototype.revealFrame = function (box, frame) {
    var host = box.getAttribute('data-pg-video-host');
    var done = false;
    function ready() {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMessage);
      box.classList.add('is-ready');
    }
    function onMessage(e) {
      if (e.source !== frame.contentWindow) return;
      if (host !== 'youtube') return ready();
      var data = e.data;
      try {
        if (typeof data === 'string') data = JSON.parse(data);
      } catch (err) {
        return;
      }
      if (!data) return;
      var state = data.event === 'onStateChange' ? data.info : data.info && data.info.playerState;
      if (state === 1) ready();
    }
    window.addEventListener('message', onMessage);
    frame.addEventListener('load', function () {
      if (frame.contentWindow) {
        frame.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), '*');
      }
      setTimeout(ready, 2500);
    });
    setTimeout(ready, 6000);
  };

  Gallery.prototype.pauseVideo = function () {
    var box = this.videoBox();
    var player = box && box.querySelector('iframe, video');
    if (player) this.command(box, player, 'pause');
  };

  // Shopify video: the element. YouTube (enablejsapi=1) and Vimeo: their
  // postMessage commands
  Gallery.prototype.command = function (box, player, action) {
    if (player.tagName === 'VIDEO') {
      if (action === 'pause') {
        player.pause();
      } else {
        var p = player.play();
        if (p && p.catch) p.catch(function () {});
      }
      return;
    }
    if (!player.contentWindow) return;
    var host = box.getAttribute('data-pg-video-host');
    var message = host === 'vimeo'
      ? { method: action }
      : { event: 'command', func: action === 'pause' ? 'pauseVideo' : 'playVideo', args: '' };
    player.contentWindow.postMessage(JSON.stringify(message), '*');
  };

  /* Setup ------------------------------------------------------------------ */

  function init() {
    var roots = document.querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].productGallery) roots[i].productGallery = new Gallery(roots[i]);
    }
  }

  // Public: show a variant's photo (selectCallback in product-template).
  // Accepts the variant or an image id; tries the media id, the image id and
  // the file name.
  function select(variant) {
    init();
    var root = document.querySelector(ROOT);
    var g = root && root.productGallery;
    if (!g || !variant) return;
    var mediaId = variant.featured_media && variant.featured_media.id;
    var image = typeof variant === 'object' ? variant.featured_image : { id: variant };
    if (!mediaId && !image) return;
    var key = image && image.src ? fileKey(image.src) : '';
    for (var i = 0; i < g.slides.length; i++) {
      var s = g.slides[i];
      if ((mediaId && s.getAttribute('data-media-id') === String(mediaId)) ||
          (image && image.id && s.getAttribute('data-image-id') === String(image.id)) ||
          (key && s.getAttribute('data-pg-file') === key)) {
        if (i === g.index) return;
        g.setTab('photos');
        g.show(i, true);
        return;
      }
    }
  }

  // Scripts at the end of <body> (theme.js with DotPager) run before this
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section: set up the new markup
  document.addEventListener('shopify:section:load', init);

  // Theme editor: selecting a "Guía de medidas" block opens the guide if it's
  // the one this product shows; deselecting it closes it
  function editorGuide(e, open) {
    var id = e.detail && e.detail.blockId;
    var guide = id && document.querySelector('[data-pg-guide-block="' + id + '"]');
    var root = guide && guide.closest(ROOT);
    var g = root && root.productGallery;
    if (!g) return;
    if (open) g.openGuide();
    else g.closeGuide();
  }
  document.addEventListener('shopify:block:select', function (e) {
    editorGuide(e, true);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    editorGuide(e, false);
  });
  // Re-rendered with the guide open: the page must scroll again
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-pg-guide]')) {
      document.documentElement.classList.remove('pg-guide-open');
    }
  });

  window.ProductGallery = { select: select };
})();
